import { AxiosError } from 'axios';
import { classifyTokenizeOutcomeFromAxiosError } from '@/common/services/NoviLogging';
import { getFiservErrorMessage, FiservApiErrorResponse } from '../types/FiservApiErrorResponse';
import { getQboErrorMessage, QboApiErrorResponse } from '../types/QboApiErrorResponse';

/**
 * When QBP/Fiserv tokenization fails with outcome network_no_response (PostHog: transport
 * blocked before any HTTP response — ad blockers, privacy browsers, VPN, firewall), the
 * processor returns no body and getQboErrorMessage / getFiservErrorMessage fall back to a
 * generic retry message. Show actionable browser/network guidance only for that outcome;
 * timeout, cancel, and unknown transport failures use the generic fallback until validated.
 * In production, network_no_response manifests as axios message "Network Error" (XHR onerror).
 */

export type TokenizeProcessor = 'QuickBooksPayments' | 'Fiserv';

const TRANSPORT_NETWORK_MESSAGE =
    'Your browser may be blocking the payment connection. Try disabling ad blockers or privacy extensions for this site, or use another browser or network.';

export function getTokenizeFailureDetail(
    error: AxiosError,
    processorResponseData: unknown,
    options: { processor: TokenizeProcessor; isCard?: boolean }
): string {
    if (error?.response) {
        if (options.processor === 'QuickBooksPayments') {
            return getQboErrorMessage(processorResponseData as QboApiErrorResponse);
        }
        return getFiservErrorMessage(
            processorResponseData as FiservApiErrorResponse,
            options.isCard ?? false
        );
    }
    if (classifyTokenizeOutcomeFromAxiosError(error) === 'network_no_response') {
        return TRANSPORT_NETWORK_MESSAGE;
    }
    return options.processor === 'QuickBooksPayments'
        ? getQboErrorMessage(undefined)
        : getFiservErrorMessage(undefined, options.isCard ?? false);
}
